import { createStore, combineReducers } from 'redux';
import pluralize from 'pluralize';
import validate from '../schema/validate';
import remote from './remote';
import Socket from './socket';

pluralize.addSingularRule(/data$/i, 'data');
const storeProvider = (process.env.NODE_ENV === 'test' ? require('./storeMock').default : createStore);

const apiBase = '/api/';

const searchParams = new URLSearchParams(location.search);
let stateNum = 0;

const RESTMethods = {
  get: remote.get,
  patch: remote.patch,
  post: remote.post,
  delete: remote.delete
};

const socket = Socket.initialize();

const paginationFields = ['pageNumber', 'pageSize', 'orderBy', 'orderDirection'];

const urlEncode = (rawValue, schema) => {
  switch (schema.definition) {
    case 'jsDate':
      return new Date(rawValue).toISOString();
    default:
      return rawValue;
  }
};

const setHistory = (name, value, schema) => {
  if (typeof name === 'object') {
    Object.keys(name).forEach((k) => (name[k] ? searchParams.set(k, urlEncode(name[k], schema)) : searchParams.delete(k)));
  } else {
    value ? searchParams.set(name, urlEncode(value, schema)) : searchParams.delete(name);
  }
  const searchString = searchParams.toString();
  const withPrefix = searchString ? `?${searchString}` : '';
  if (withPrefix !== location.search) {
    stateNum++;
    history.pushState({ page: stateNum }, '', withPrefix);
  }
};

const toQueryString = (params) => {
  if (!params || Object.keys(params).length === 0) return '';
  const { id, ...rest } = params;
  return Object.keys(rest).reduce((s, k) => `${s}${k}=${params[k]}&`, '?').slice(0, -1);
};

export const RESTRoutes = {
  index: (resource) => `${apiBase}${resource}`,
  show: (resource, id, params) => `${apiBase}${resource}/${id}${toQueryString(params)}`,
  create: (resource) => `${apiBase}${resource}`,
  update: (resource, id) => `${apiBase}${resource}/${id}`,
  delete: (resource, id, params) => `${apiBase}${resource}/${id}${toQueryString(params)}`
};

const dirtyValuesFor = (currentValue, newValue) => {
  const dirtyValues = {};
  Object.keys(newValue).forEach((field) => {
    if (field !== 'id' && JSON.stringify(currentValue?.[field]) !== JSON.stringify(newValue[field])) {
      dirtyValues[field] = newValue[field];
    }
  });
  return dirtyValues;
};

const toCamelCase = (snakeCasedString) => (
  snakeCasedString.split(/_(?=[a-z])/g).map((s, i) => (i === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1))).join('')
);

const paginateRoute = (baseRoute, store, name) => {
  const camelName = toCamelCase(name);
  const { pageNumber, pageSize, orderBy, orderDirection } = store.getState()[`${camelName}Pagination`];
  const firstChar = /\?/.test(baseRoute) ? '&' : '?';
  return `${baseRoute}${firstChar}page_number=${pageNumber}&page_size=${pageSize}&order_by=${orderBy}&order_direction=${orderDirection}`;
};

const postPayloadFromParams = (route) => [...new URLSearchParams(route.split('?')[1])].reduce(
  (a, [k, v]) => ((a[k] = v), a),
  {}
);

const typeCast = (rawValue, schema) => {
  if (rawValue && schema.type === 'array') {
    return rawValue.split(',').map((v) => typeCast(v, { definition: schema.definition, type: 'primitive' }));
  }
  if (rawValue && schema.definition === 'integer') return parseInt(rawValue, 10);
  if (rawValue && schema.definition === 'jsDate') return new Date(rawValue);
  return rawValue;
};

// resources = list of {name: [pluralized  snake_cased resource name], isRemote: boolean, defaultValue: [default value]}
const StateManager = (resources) => {
  const manager = { resources, getStoredRequest: remote.getStoredRequest, addFakeResponse: remote.addResponse };
  const reducer = {};
  const validators = {};
  manager.watchHistory = {};

  resources.forEach(({ name, defaultValue, schema, isPaginated, useHistory }) => {
    const camelName = toCamelCase(name);
    validators[camelName] = schema;
    if (defaultValue) Object.keys(defaultValue).length > 0 && validate(name, schema, defaultValue);
    const actualDefaultValue = (useHistory && !isPaginated && typeCast(searchParams.get(name), schema)) || schema?.defaultValue || defaultValue;
    reducer[camelName] = (state = actualDefaultValue, { type, val }) => {
      if (type === name) return val;
      return state;
    };
    if (isPaginated) {
      const defaultPagination = { pageNumber: 0, pageSize: 15, total: 0, orderBy: '', orderDirection: '' };
      if (useHistory) {
        paginationFields.forEach((k) => {
          if (searchParams.get(k)) defaultPagination[k] = searchParams.get(k);
        });
      }
      defaultPagination.pageNumber = parseInt(defaultPagination.pageNumber) || defaultPagination.pageNumber;
      defaultPagination.pageSize = parseInt(defaultPagination.pageSize) || defaultPagination.pageSize;
      reducer[`${camelName}Pagination`] = (state = defaultPagination, { type, val }) => {
        if (type === `${name}_pagination`) return { ...state, ...val };
        return state;
      };
    }
  });

  const combinedReducer = combineReducers(reducer);
  const topLevelReducer = (state, action) => {
    if (action.type === 'SET_ALL') return { ...state, ...action.values };
    return combinedReducer(state, action);
  };

  const store = storeProvider(topLevelReducer);
  manager.set = (values) => {
    Object.keys(values).forEach((k) => {
      const resourceDef = resources.find((r) => r.name === k);
      if (resourceDef && resourceDef.useHistory && !resourceDef.isPaginated) {
        setHistory(k, values[k], resourceDef.schema);
      }
      validate(k, validators[k], values[k]);
    });
    store.dispatch({ type: 'SET_ALL', values });
  };

  resources.forEach((params) => {
    const { name, isRemote, isSingleton, schema, isPaginated, usePost, useHistory, onFetch } = params;
    const setterFunctionName = toCamelCase(`set_${name}`);
    const singularName = pluralize.singular(name);
    manager[setterFunctionName] = (val) => {
      validate(name, schema, val);

      if (schema?.definition === 'jsDate') {
        val = new Date(val)
      }

      store.dispatch({
        type: name,
        val
      });
      params.refresh?.forEach((toRefresh) => {
        const getterFunctionName = toCamelCase(`fetch_${toRefresh}`);
        manager[getterFunctionName]();
      });
      if (!isPaginated && useHistory) setHistory(name, val, schema);
    };
    if (isPaginated) {
      manager[`${setterFunctionName}Pagination`] = (values) => {
        store.dispatch({
          type: `${name}_pagination`,
          val: values
        });
        if (values.pageNumber || values.pageSize || values.orderBy || values.orderDirection) {
          if (useHistory) {
            const historyVals = {};
            paginationFields.forEach((k) => historyVals[k] = values[k]);
            setHistory(historyVals, null, schema);
          }
          return manager[toCamelCase(`fetch_${name}`)]();
        }
      };
      if (useHistory) {
        manager.watchHistory.pagination = manager[`${setterFunctionName}Pagination`];
      }
    } else if (useHistory) {
      manager.watchHistory[name] = manager[setterFunctionName];
    }
    if (isRemote) {
      const getterFunctionName = toCamelCase(`fetch_${name}`);
      const pluralized = pluralize.plural(name);
      if (isSingleton) {
        manager[getterFunctionName] = (id, queryParams) => {
          const route = params.show ? params.show(store.getState(), id) : RESTRoutes.show(pluralized, id, queryParams);

          let promise = null;
          if (usePost) {
            promise = RESTMethods.post(route.split('?')?.[0], postPayloadFromParams(route));
          } else {
            promise = RESTMethods.get(route);
          }
          if (onFetch) onFetch[0](onFetch[1]);
          promise.then((r) => {
            if (r.headers && r.headers['x-live-data']) socket.register(r.headers['x-live-data'], manager[setterFunctionName]);
            manager[setterFunctionName](r.data);
          });
          if (onFetch) promise.finally(() => onFetch[0](onFetch[2]));
          return promise;
        };
      } else {
        manager[getterFunctionName] = () => {
          const baseRoute = params.index ? params.index(store.getState()) : RESTRoutes.index(name);
          const route = isPaginated ? paginateRoute(baseRoute, store, name) : baseRoute;

          let promise = null;

          if (usePost) {
            promise = RESTMethods.post(route.split('?')?.[0], postPayloadFromParams(route));
          } else {
            promise = RESTMethods.get(route);
          }
          if (onFetch) onFetch[0](onFetch[1]);
          const injectData = (data) => {
            if (Array.isArray(data)) manager[setterFunctionName](data);
            if (isPaginated && (data.entries || data.list) && (data.total !== undefined || data.meta !== undefined)) {
              manager[setterFunctionName](data.entries || data.list);
              manager[`${setterFunctionName}Pagination`]({ total: data.total, meta: data.meta });
            }
          };
          promise.then((r) => {
            if (r.headers && r.headers['x-live-data']) socket.register(r.headers['x-live-data'], injectData);
            injectData(r.data);
          });
          if (onFetch) promise.finally(() => onFetch[0](onFetch[2]));
          return promise;
        };
      }

      const saveFunctionName = toCamelCase(`save_${singularName}`);
      manager[saveFunctionName] = (resource) => {
        if (resource.id) {
          const route = params.update ? params.update(resource) : RESTRoutes.update(pluralized, resource.id);
          const currentVal = store.getState()[toCamelCase(name)];
          return RESTMethods.patch(route, { [singularName]: dirtyValuesFor(currentVal, resource) });
        }
        const route = params.create ? params.create() : RESTRoutes.create(pluralized);
        return RESTMethods.post(route, { [singularName]: resource });
      };
      const deleteFunctionName = toCamelCase(`delete_${singularName}`);
      manager[deleteFunctionName] = (resource) => {
        if (resource.id) {
          const { id, ...rest } = resource;
          const route = params.delete ? params.delete(resource) : RESTRoutes.delete(pluralized, resource.id, rest);
          return RESTMethods.delete(route);
        }
      };
    }
  });

  manager.store = store;

  const currentOnPopState = window.onpopstate;
  window.onpopstate = (event) => {
    currentOnPopState?.(event);
    const newSearchParams = new URLSearchParams(location.search);
    const defaultPagination = { pageNumber: 0, pageSize: 15, total: 0, orderBy: '', orderDirection: '' };
    newSearchParams.forEach((value, key) => {
      if (manager.watchHistory.pagination && defaultPagination[key]) {
        defaultPagination[key] = value;
      } else {
        manager.watchHistory[key]?.(value);
      }
    });
    if (manager.watchHistory.pagination) manager.watchHistory.pagination(defaultPagination);
  };

  return manager;
};

export default StateManager;
